import {
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import styles from './forms.module.css';

/** O que o controle precisa para ficar ligado ao rótulo, à dica e ao erro (nome, descrição, validade). */
interface Wiring {
  id: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string | undefined;
}

interface FrameProps {
  label: ReactNode;
  /** Regra do campo, mostrada ANTES de errar e lida junto com o campo (`aria-describedby`). */
  hint?: ReactNode;
  /** Erro do campo: texto abaixo dele e `aria-invalid`. Quem valida leva o foco até o campo. */
  error?: string | null | undefined;
}

/** Rótulo, dica e erro em volta de um controle (campo de texto, área de texto ou lista de opções). */
function Frame({
  label,
  hint,
  error,
  children,
}: FrameProps & { children: (wiring: Wiring) => ReactElement }): ReactElement {
  const uid = useId();
  const inputId = `${uid}-input`;
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className={styles.field}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {children({
        id: inputId,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy || undefined,
      })}
      {error && (
        <p id={errorId} className={styles.fieldError}>
          {error}
        </p>
      )}
    </div>
  );
}

type Owned = 'id' | 'className' | 'aria-invalid' | 'aria-describedby';

interface FieldProps extends FrameProps, Omit<InputHTMLAttributes<HTMLInputElement>, Owned> {
  ref?: Ref<HTMLInputElement>;
}

/** Campo de texto rotulado, com dica e erro ligados ao `<input>` (nome, descrição e validade). */
export function Field({ label, hint, error, ref, ...input }: FieldProps): ReactElement {
  return (
    <Frame label={label} hint={hint} error={error}>
      {(wiring) => <input {...input} {...wiring} ref={ref} className={styles.input} />}
    </Frame>
  );
}

interface TextAreaFieldProps
  extends FrameProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, Owned> {
  ref?: Ref<HTMLTextAreaElement>;
}

/** Como o `Field`, para texto de várias linhas (a mensagem simulada). */
export function TextAreaField({
  label,
  hint,
  error,
  ref,
  ...textarea
}: TextAreaFieldProps): ReactElement {
  return (
    <Frame label={label} hint={hint} error={error}>
      {(wiring) => (
        <textarea
          {...textarea}
          {...wiring}
          ref={ref}
          className={`${styles.input} ${styles.area}`}
        />
      )}
    </Frame>
  );
}

interface SelectFieldProps
  extends FrameProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, Owned> {
  ref?: Ref<HTMLSelectElement>;
}

/** Como o `Field`, para escolher uma opção de uma lista (o número da simulação). */
export function SelectField({
  label,
  hint,
  error,
  ref,
  children,
  ...select
}: SelectFieldProps): ReactElement {
  return (
    <Frame label={label} hint={hint} error={error}>
      {(wiring) => (
        <select {...select} {...wiring} ref={ref} className={styles.input}>
          {children}
        </select>
      )}
    </Frame>
  );
}
